import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("esg_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const opts = () => ({ headers: getHeaders(), withCredentials: true });

export const api = {
  createReport: (data) => axios.post(`${API}/reports`, data, opts()).then(r => r.data),
  listReports: () => axios.get(`${API}/reports`, opts()).then(r => r.data),
  getReport: (id) => axios.get(`${API}/reports/${id}`, opts()).then(r => r.data),
  updateReport: (id, data) => axios.put(`${API}/reports/${id}`, data, opts()).then(r => r.data),
  deleteReport: (id) => axios.delete(`${API}/reports/${id}`, opts()).then(r => r.data),
  generateReport: (id) => axios.post(`${API}/reports/${id}/generate`, {}, opts()).then(r => r.data),
  validateReport: (id) => axios.post(`${API}/reports/${id}/validate`, {}, opts()).then(r => r.data),
  regenerateCeoLetter: (id) => axios.post(`${API}/reports/${id}/regenerate-ceo-letter`, {}, { ...opts(), timeout: 180000 }).then(r => r.data),
  refreshPdf: (id) => axios.post(`${API}/reports/${id}/refresh-pdf`, {}, opts()).then(r => r.data),
  getReportStatus: (id) => axios.get(`${API}/reports/${id}/status`, opts()).then(r => r.data),
  pollUntilGenerated: async (id, onStatus) => {
    // Start generation
    await axios.post(`${API}/reports/${id}/generate`, {}, opts());
    // Capture the token at start. If it changes mid-poll, another tab
    // has logged in with a different account, the polling loop would
    // start hitting 404s (report belongs to the original user, not the
    // new one). Abort with a specific, actionable error.
    const startToken = localStorage.getItem("esg_token") || "";
    // Poll for completion, generation can take 7-12 min; we allow up to 20 min before giving up.
    // Note: even if we time out here, the server keeps generating, user can click Regenerate again
    // and the status will correctly reflect whatever the server has.
    const MAX_ATTEMPTS = 240; // 240 × 5s = 20 minutes
    let attempts = 0;
    while (attempts < MAX_ATTEMPTS) {
      await new Promise(r => setTimeout(r, 5000));
      // Cross-tab session collision check
      const currentToken = localStorage.getItem("esg_token") || "";
      if (currentToken !== startToken) {
        const err = new Error("session_changed");
        err.session_changed = true;
        throw err;
      }
      try {
        const { data } = await axios.get(`${API}/reports/${id}/status`, opts());
        if (onStatus) onStatus(data.status, attempts, data.phase_label, data.phase);
        if (data.status === "generated") return data;
        if (data.status === "error") throw new Error(data.error || "Generation failed");
      } catch (e) {
        // Transient network error during poll, don't kill the whole flow, just retry
        if (e.message === "Generation failed" || (e.response && e.response.status === 404)) throw e;
      }
      attempts++;
    }
    // Polling timed out, but generation continues server-side. Return a
    // special "still-running" status instead of throwing, caller decides UX.
    return { status: "still_running", _polling_timeout: true };
  },
  pdfStatus: (id, lang) => axios.get(`${API}/reports/${id}/pdf/status?lang=${lang || 'en'}`, opts()).then(r => r.data),
  // Direct download URL with JWT in query string. Used by mobile <a href>
  // links so the device's browser handles the download natively (no JS,
  // no blob URL, no preview, just Content-Disposition: attachment from
  // the backend).
  pdfUrl: (id, lang, inline = false) => {
    const token = localStorage.getItem("esg_token") || "";
    const langParam = lang ? `lang=${encodeURIComponent(lang)}&` : '';
    const inlineParam = inline ? `inline=1&` : '';
    return `${API}/reports/${id}/pdf?${langParam}${inlineParam}token=${encodeURIComponent(token)}`;
  },
  downloadPdf: async (id, lang, attempt = 1) => {
    const langParam = lang ? `?lang=${lang}` : '';

    // Mobile direct-download path. iOS Safari + Android Chrome don't reliably
    // honour the `<a download>` attribute on blob URLs, and the user wants a
    // real download (not an in-browser preview). We navigate the device
    // directly to the backend endpoint with the JWT in the query string. The
    // backend responds with `Content-Disposition: attachment` so the OS
    // triggers a real save.
    const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent);
    if (isMobile) {
      const token = localStorage.getItem("esg_token") || "";
      const sep = langParam ? '&' : '?';
      const directUrl = `${API}/reports/${id}/pdf${langParam}${sep}token=${encodeURIComponent(token)}`;
      // No polling, no waiting, just navigate. Backend returns 200 with
      // Content-Disposition: attachment when cache hot (instant download), or
      // 202 + JSON if cache cold. If cache cold the user will see a brief
      // "rendering" message and can tap again. We also kick the cache here
      // by firing a one-shot fetch in parallel so the next tap is instant.
      try {
        fetch(`${API}/reports/${id}/pdf${langParam}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        }).catch(() => {});
      } catch {}
      window.location.href = directUrl;
      return;
    }

    // Desktop path, fetch as blob then synthesise a save dialog with <a download>.
    let response;
    try {
      response = await axios.get(`${API}/reports/${id}/pdf${langParam}`, {
        ...opts(),
        responseType: 'blob',
        timeout: 900000,
        validateStatus: (s) => (s >= 200 && s < 300) || s === 202,
      });
    } catch (err) {
      const isRetryable = err?.code === "ERR_NETWORK" || err?.code === "ECONNABORTED" ||
                          (err?.response?.status >= 502 && err?.response?.status <= 504) ||
                          (err?.response?.status >= 520 && err?.response?.status <= 527);
      if (isRetryable && attempt < 30) {
        await new Promise(r => setTimeout(r, 3000));
        return await api.downloadPdf(id, lang, attempt + 1);
      }
      throw err;
    }

    // Backend returns 202 when render is in progress → poll status, then retry.
    if (response.status === 202) {
      // Wait + poll up to ~3 minutes
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const st = await api.pdfStatus(id, lang || 'en');
          if (st?.ready) break;
        } catch {}
      }
      return await api.downloadPdf(id, lang, 1); // restart fresh
    }

    const blob = response.data;
    if (!blob || blob.size < 1024) {
      const text = blob ? await blob.text() : "";
      throw new Error(text.slice(0, 200) || "Download empty");
    }

    const pdfBlob = new Blob([blob], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(pdfBlob);
    const disposition = response.headers['content-disposition'];
    const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'ESG_Report.pdf';

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  updateBranding: (id, data) => axios.put(`${API}/reports/${id}/branding`, data, opts()).then(r => r.data),
  uploadLogo: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/reports/${id}/logo`, formData, {
      ...opts(),
      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  },
  deleteLogo: (id) => axios.delete(`${API}/reports/${id}/logo`, opts()).then(r => r.data),
  // Section images
  uploadSectionImage: async (id, section, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/reports/${id}/images/${section}`, formData, {
      ...opts(),
      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  },
  deleteSectionImage: (id, section, index) => axios.delete(`${API}/reports/${id}/images/${section}/${index}`, opts()).then(r => r.data),
  getSectionImages: (id, section) => axios.get(`${API}/reports/${id}/images/${section}`, opts()).then(r => r.data),
  // Translation
  translateReport: (id, targetLang) => axios.post(`${API}/reports/${id}/translate/${targetLang}`, {}, opts()).then(r => r.data),
  pollUntilTranslated: async (id, onStatus) => {
    let attempts = 0;
    while (attempts < 300) {
      await new Promise(r => setTimeout(r, 6000));
      const { data } = await axios.get(`${API}/reports/${id}/status`, opts());
      const ts = data.translation_status;
      if (onStatus) onStatus(ts);
      if (ts === "done") return data;
      if (ts === "error") throw new Error(data.error || "Translation failed");
      attempts++;
    }
    throw new Error("Translation timed out");
  },
  // Admin
  adminUsers: () => axios.get(`${API}/admin/users`, opts()).then(r => r.data),
  adminStats: () => axios.get(`${API}/admin/stats`, opts()).then(r => r.data),
  adminSampleDownloads: () => axios.get(`${API}/admin/sample-downloads`, opts()).then(r => r.data),
  adminGenerationHealth: () => axios.get(`${API}/admin/generation-health`, opts()).then(r => r.data),
  adminRetryReport: (id) => axios.post(`${API}/admin/reports/${id}/retry`, {}, opts()).then(r => r.data),
  adminDeleteUser: (id) => axios.delete(`${API}/admin/users/${id}`, opts()).then(r => r.data),
  adminGrandfather: (id, grandfathered) => axios.post(`${API}/admin/subscribers/${id}/grandfather`, { grandfathered }, opts()).then(r => r.data),
  adminSubscribers: () => axios.get(`${API}/admin/subscribers`, opts()).then(r => r.data),
  adminNewsUsage: () => axios.get(`${API}/admin/news-usage`, opts()).then(r => r.data),
  adminTrafficStats: (range = "30d") => axios.get(`${API}/admin/traffic-stats`, { ...opts(), params: { range } }).then(r => r.data),
  adminCountryBreakdown: () => axios.get(`${API}/admin/country-breakdown`, opts()).then(r => r.data),
  adminCancelSubscription: (id) => axios.post(`${API}/admin/subscribers/${id}/cancel`, {}, opts()).then(r => r.data),
  adminRefundSubscription: (id, amount = null) => axios.post(`${API}/admin/subscribers/${id}/refund`, amount ? { amount } : {}, opts()).then(r => r.data),
  adminLoginAudit: (limit = 100, email = null) => axios.get(`${API}/admin/audit/logins`, { ...opts(), params: { limit, ...(email ? { email } : {}) } }).then(r => r.data),
  changePassword: (current_password, new_password) => axios.post(`${API}/auth/change-password`, { current_password, new_password }, opts()).then(r => r.data),
};